/**
 * Book Resources reader — sections/book-resources.liquid
 * Card action ([data-book-open]) opens the <dialog data-book-reader> and renders the PDF
 * page by page with PDF.js, loaded from data-pdfjs-src on first open only.
 * No <dialog> support → no-op: the action stays a plain link to the PDF (new tab).
 */
(function () {
  'use strict';

  var PDFJS_TIMEOUT_MS = 15000;
  var VIEWER_INSET_PX = 32;
  var RESIZE_DEBOUNCE_MS = 200;
  var SCROLL_LOCK_CLASS = 'book-reader-open';

  function init() {
    var reader = document.querySelector('[data-book-reader]');
    if (!reader || reader.getAttribute('data-book-reader-init') === 'true') return;
    if (typeof reader.showModal !== 'function') return;
    reader.setAttribute('data-book-reader-init', 'true');

    var els = {
      title: reader.querySelector('[data-book-reader-title]'),
      meta: reader.querySelector('[data-book-reader-meta]'),
      viewer: reader.querySelector('[data-book-reader-viewer]'),
      canvas: reader.querySelector('[data-book-reader-canvas]'),
      status: reader.querySelector('[data-book-reader-status]'),
      pager: reader.querySelector('[data-book-reader-pager]'),
      page: reader.querySelector('[data-book-reader-page]'),
      prev: reader.querySelector('[data-book-reader-prev]'),
      next: reader.querySelector('[data-book-reader-next]'),
      save: reader.querySelector('[data-book-reader-save]'),
      open: reader.querySelector('[data-book-reader-open]'),
      close: reader.querySelector('[data-book-reader-close]')
    };

    var state = {
      token: 0,
      task: null,
      pdf: null,
      page: 1,
      total: 0,
      rendering: false,
      pendingPage: null,
      trigger: null
    };
    var pdfjsPromise = null;
    var resizeTimer = null;

    function loadPdfjs() {
      if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
      if (pdfjsPromise) return pdfjsPromise;
      pdfjsPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        var timer = window.setTimeout(function () {
          reject(new Error('PDF.js load timed out'));
        }, PDFJS_TIMEOUT_MS);
        script.src = reader.getAttribute('data-pdfjs-src');
        script.async = true;
        script.onload = function () {
          window.clearTimeout(timer);
          if (!window.pdfjsLib) {
            reject(new Error('PDF.js unavailable'));
            return;
          }
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = reader.getAttribute('data-pdfjs-worker');
          resolve(window.pdfjsLib);
        };
        script.onerror = function () {
          window.clearTimeout(timer);
          reject(new Error('PDF.js failed to load'));
        };
        document.head.appendChild(script);
      });
      pdfjsPromise.catch(function () {
        pdfjsPromise = null;
      });
      return pdfjsPromise;
    }

    function fileNameFromUrl(url) {
      var path = url.split('?')[0].split('#')[0];
      var name = path.substring(path.lastIndexOf('/') + 1);
      try {
        return decodeURIComponent(name);
      } catch (e) {
        return name;
      }
    }

    function setStatus(key) {
      if (!key) {
        els.status.hidden = true;
        els.status.textContent = '';
        return;
      }
      els.status.textContent = els.status.getAttribute(key === 'error' ? 'data-error-text' : 'data-loading-text');
      els.status.hidden = false;
    }

    function updatePager() {
      if (state.total <= 1) {
        els.pager.hidden = true;
        return;
      }
      els.page.textContent = els.pager.getAttribute('data-page-format')
        .replace('{current}', state.page)
        .replace('{total}', state.total);
      els.prev.disabled = state.page <= 1;
      els.next.disabled = state.page >= state.total;
      els.pager.hidden = false;
    }

    function renderPage(num) {
      var token = state.token;
      var pdf = state.pdf;
      if (!pdf) return;
      state.rendering = true;

      pdf.getPage(num).then(function (page) {
        if (token !== state.token) return null;
        var base = page.getViewport({ scale: 1 });
        var availW = els.viewer.clientWidth - VIEWER_INSET_PX;
        var availH = els.viewer.clientHeight - VIEWER_INSET_PX;
        var scale = Math.min(availW / base.width, availH / base.height);
        if (!(scale > 0)) scale = 1;
        var ratio = window.devicePixelRatio || 1;
        var viewport = page.getViewport({ scale: scale * ratio });
        var canvas = els.canvas;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = Math.floor(viewport.width / ratio) + 'px';
        canvas.style.height = Math.floor(viewport.height / ratio) + 'px';
        return page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }).promise;
      }).then(function () {
        if (token !== state.token) return;
        state.rendering = false;
        setStatus(null);
        els.canvas.hidden = false;
        if (state.pendingPage !== null) {
          var pending = state.pendingPage;
          state.pendingPage = null;
          renderPage(pending);
        }
      }).catch(function () {
        if (token !== state.token) return;
        state.rendering = false;
        els.canvas.hidden = true;
        setStatus('error');
      });
    }

    function goToPage(num) {
      if (!state.pdf || num < 1 || num > state.total || num === state.page) return;
      state.page = num;
      updatePager();
      if (state.rendering) {
        state.pendingPage = num;
      } else {
        renderPage(num);
      }
    }

    function teardown() {
      state.token += 1;
      if (state.task) {
        state.task.destroy();
        state.task = null;
      }
      state.pdf = null;
      state.total = 0;
      state.page = 1;
      state.rendering = false;
      state.pendingPage = null;
    }

    function openReader(trigger) {
      var url = trigger.getAttribute('href');
      var meta = trigger.getAttribute('data-book-meta') || '';

      teardown();
      state.trigger = trigger;
      els.title.textContent = trigger.getAttribute('data-book-title') || '';
      els.meta.textContent = meta;
      els.meta.hidden = !meta;
      els.save.setAttribute('href', url);
      els.save.setAttribute('download', fileNameFromUrl(url));
      els.open.setAttribute('href', url);
      els.canvas.hidden = true;
      els.pager.hidden = true;
      setStatus('loading');

      reader.showModal();
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);

      var token = state.token;
      loadPdfjs().then(function (pdfjsLib) {
        if (token !== state.token) return null;
        state.task = pdfjsLib.getDocument({ url: url, isEvalSupported: false });
        return state.task.promise;
      }).then(function (pdf) {
        if (!pdf || token !== state.token) return;
        state.pdf = pdf;
        state.total = pdf.numPages;
        state.page = 1;
        updatePager();
        renderPage(1);
      }).catch(function () {
        if (token !== state.token) return;
        setStatus('error');
      });
    }

    document.addEventListener('click', function (event) {
      var target = event.target;
      var trigger = target && target.closest ? target.closest('[data-book-open]') : null;
      if (!trigger) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openReader(trigger);
    });

    els.prev.addEventListener('click', function () {
      goToPage(state.page - 1);
    });
    els.next.addEventListener('click', function () {
      goToPage(state.page + 1);
    });
    els.close.addEventListener('click', function () {
      reader.close();
    });

    reader.addEventListener('click', function (event) {
      if (event.target === reader) reader.close();
    });

    reader.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') goToPage(state.page - 1);
      if (event.key === 'ArrowRight') goToPage(state.page + 1);
    });

    reader.addEventListener('close', function () {
      teardown();
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      if (state.trigger && typeof state.trigger.focus === 'function') state.trigger.focus();
      state.trigger = null;
    });

    window.addEventListener('resize', function () {
      if (!reader.open || !state.pdf) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (state.rendering) {
          state.pendingPage = state.page;
        } else {
          renderPage(state.page);
        }
      }, RESIZE_DEBOUNCE_MS);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
